import { createContext, useContext, useEffect, useState, useCallback } from "react";

// ─── Context ──────────────────────────────────────────────────────────────────

const ThemeContext = createContext({
  theme: "light",
  toggleTheme: () => {},
  isDark: false,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Aplica o tema no DOM imediatamente (sem esperar o useEffect).
 * Isso elimina o flash de tema errado no primeiro render.
 */
const applyTheme = (theme) => {
  const root = document.documentElement;

  // 1. data-theme no <html> — usado pelos seletores CSS dos tokens
  root.setAttribute("data-theme", theme);

  // 2. Classe no <body> — fallback para seletores que precisem de body
  document.body.classList.remove("theme-light", "theme-dark");
  document.body.classList.add(`theme-${theme}`);

  // 3. Força background e color no html e body diretamente via style
  //    Isso garante que mesmo antes do CSS processar o data-theme,
  //    o browser já pinte com a cor correta.
  if (theme === "dark") {
    root.style.backgroundColor = "#0D1829";
    root.style.color = "#E8F0FE";
    document.body.style.backgroundColor = "#0D1829";
    document.body.style.color = "#E8F0FE";
  } else {
    root.style.backgroundColor = "#F4F7FC";
    root.style.color = "#111827";
    document.body.style.backgroundColor = "#F4F7FC";
    document.body.style.color = "#111827";
  }

  localStorage.setItem("fluxoeasy-theme", theme);
};

/**
 * Lê a preferência salva ou do sistema operacional.
 * Executado ANTES do primeiro render para evitar flash.
 */
const getInitialTheme = () => {
  const saved = localStorage.getItem("fluxoeasy-theme");
  if (saved === "light" || saved === "dark") return saved;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

// Aplica o tema IMEDIATAMENTE antes mesmo do React montar
// (evita flash de tema errado)
const initialTheme = getInitialTheme();
applyTheme(initialTheme);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(initialTheme);

  // Sincroniza o DOM sempre que o tema mudar
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === "light" ? "dark" : "light"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isDark: theme === "dark" }}>
      {children}
    </ThemeContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useTheme = () => useContext(ThemeContext);